import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faClock,
  faTag,
  faFilter,
  faSortAmountDown,
  faInfoCircle,
} from "@fortawesome/free-solid-svg-icons";
import { db } from "../../../services/firebase";
import { collection, getDocs } from "firebase/firestore";
import "../../../styles/pages/services.css";

// Definiranje interface za Service objekt
interface Service {
  id: string;
  name: string;
  description?: string;
  price: number;
  salePrice?: number;
  isOnSale: boolean;
  discountPercentage?: number;
  duration?: number;
  category?: string;
}

// Komponenta za pojedinačnu uslugu
const ServiceCard: React.FC<{ service: Service }> = ({ service }) => {
  return (
    <div
      className={`service-item ${service.isOnSale ? "service-item-sale" : ""}`}
      id={service.id}
    >
      {service.isOnSale && (
        <div className="service-badge">
          <FontAwesomeIcon icon={faTag} />
          <span>{service.discountPercentage}% popusta</span>
        </div>
      )}
      <div className="service-item-content">
        <h3 className="service-item-title">{service.name}</h3>
        {service.description && (
          <p className="service-item-description">{service.description}</p>
        )}
        <div className="service-item-meta">
          <div className="service-item-price">
            {service.isOnSale ? (
              <>
                <span className="service-item-old-price">
                  {service.price} €
                </span>
                <span className="service-item-sale-price">
                  {service.salePrice} €
                </span>
              </>
            ) : (
              <span className="service-item-regular-price">
                {service.price} €
              </span>
            )}
          </div>
        </div>
        {service.duration && (
          <div className="service-item-duration">
            <FontAwesomeIcon icon={faClock} />
            <span>Očekivano vrijeme trajanja: {service.duration} min</span>
          </div>
        )}
      </div>
    </div>
  );
};

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [selectedCategory, setSelectedCategory] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("name"); // 'name', 'price-asc', 'price-desc'
  const [categories, setCategories] = useState<string[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        setLoading(true);
        const servicesCollection = collection(db, "services");
        const servicesSnapshot = await getDocs(servicesCollection);
        const servicesList = servicesSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        })) as Service[];

        // Izdvoji sve kategorije
        const uniqueCategories = Array.from(
          new Set(
            servicesList
              .map((service) => service.category)
              .filter((category): category is string => !!category)
          )
        );
        setCategories(uniqueCategories);
        setServices(servicesList);
        setLoading(false);
      } catch (error) {
        console.error("Greška pri dohvaćanju usluga: ", error);
        setLoading(false);
      }
    };

    fetchServices();
  }, []);

  // Filter i sortiranje usluga
  const filteredServices = services
    .filter((service) => {
      const matchesSearch = service.name
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === "" || service.category === selectedCategory;
      return matchesSearch && matchesCategory;
    })
    .sort((a, b) => {
      switch (sortBy) {
        case "name":
          return a.name.localeCompare(b.name, "hr");
        case "price-asc":
          return (
            (a.isOnSale ? a.salePrice! : a.price) -
            (b.isOnSale ? b.salePrice! : b.price)
          );
        case "price-desc":
          return (
            (b.isOnSale ? b.salePrice! : b.price) -
            (a.isOnSale ? a.salePrice! : a.price)
          );
        default:
          return 0;
      }
    });

  // Grupiranje usluga na snižene i redovne
  const discountedServices = filteredServices.filter(
    (service) => service.isOnSale
  );
  const regularServices = filteredServices.filter(
    (service) => !service.isOnSale
  );

  return (
    <div className="services-page">
      <section className="services-hero">
        <div className="container">
          <div className="services-hero-content">
            <h1 className="services-hero-title">Naše usluge</h1>
            <p className="services-hero-description">
              Otkrijte naš asortiman profesionalnih tretmana obrva i trepavica
              prilagođenih vašim željama i potrebama.
            </p>
          </div>
        </div>
      </section>

      <section className="services-section">
        <div className="container">
          {/* Napomene */}
          <div className="services-notes">
            <div className="services-note">
              <FontAwesomeIcon icon={faInfoCircle} className="note-icon" />
              <p>Plaćanje je moguće izvršiti isključivo gotovinom.</p>
            </div>
            <div className="services-note">
              <FontAwesomeIcon icon={faInfoCircle} className="note-icon" />
              <p>
                Nadopuna trepavica vrijedi unutar 4 tjedna, nakon toga računa se
                po cijeni ugradnje.
              </p>
            </div>
            <div className="services-note">
              <FontAwesomeIcon icon={faInfoCircle} className="note-icon" />
              <p>
                Nadopuna trepavica na tuđi rad računa se po cijeni ugradnje.
              </p>
            </div>
          </div>

          <div className="services-filter-bar">
            <div className="services-search">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                placeholder="Pretraži usluge..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <button
              className="filter-toggle-btn"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <FontAwesomeIcon icon={faFilter} />
              <span>Filteri</span>
            </button>

            <div className={`filters-dropdown ${isFilterOpen ? "active" : ""}`}>
              <div className="filters-group">
                <label htmlFor="sort-select">Sortiraj po:</label>
                <select
                  id="sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="name">Nazivu (A-Ž)</option>
                  <option value="price-asc">Cijeni (najniža prvo)</option>
                  <option value="price-desc">Cijeni (najviša prvo)</option>
                </select>
              </div>
            </div>
          </div>

          {loading ? (
            <div className="services-loading">
              <div className="spinner"></div>
              <p>Učitavanje tretmana...</p>
            </div>
          ) : (
            <div className="services-content">
              {discountedServices.length > 0 && (
                <div className="services-group">
                  <h2 className="services-group-title">
                    <FontAwesomeIcon icon={faTag} />
                    <span>Usluge na sniženju</span>
                  </h2>
                  <div className="services-grid">
                    {discountedServices.map((service) => (
                      <ServiceCard key={service.id} service={service} />
                    ))}
                  </div>
                </div>
              )}

              {regularServices.length > 0 && (
                <div className="services-group">
                  <h2 className="services-group-title">
                    <FontAwesomeIcon icon={faSortAmountDown} />
                    <span>
                      {selectedCategory ? selectedCategory : "Sve usluge"}
                    </span>
                  </h2>
                  <div className="services-grid">
                    {regularServices.map((service) => (
                      <ServiceCard key={service.id} service={service} />
                    ))}
                  </div>
                </div>
              )}

              {filteredServices.length === 0 && (
                <div className="no-services-found">
                  <p>Nema pronađenih usluga prema trenutnim filterima.</p>
                  <button
                    onClick={() => {
                      setSearchTerm("");
                      setSelectedCategory("");
                    }}
                  >
                    Poništi filtere
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Services;
